import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type PackDiff, diffIsEmpty, diffStringBundles } from "../lib/pack-diff.js";

export interface DiffFetchInput {
  kind: "ruleset" | "scene";
  packId: string;
  version: string;
}

export interface UpdateDiffModalProps {
  kind: "ruleset" | "scene";
  packId: string;
  fromVersion: string;
  toVersion: string;
  fetchPack: (
    input: DiffFetchInput,
  ) => Promise<{ strings?: { en?: Record<string, string> } } | null>;
  onAccept: () => Promise<void>;
  onCancel: () => void;
}

interface State {
  loading: boolean;
  error: string | null;
  diff: PackDiff | null;
}

export default function UpdateDiffModal(props: UpdateDiffModalProps): ReactElement {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ loading: true, error: null, diff: null });
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [prev, next] = await Promise.all([
          props.fetchPack({ kind: props.kind, packId: props.packId, version: props.fromVersion }),
          props.fetchPack({ kind: props.kind, packId: props.packId, version: props.toVersion }),
        ]);
        if (cancelled) return;
        const diff = diffStringBundles(prev?.strings?.en, next?.strings?.en);
        setState({ loading: false, error: null, diff });
      } catch (e) {
        if (cancelled) return;
        setState({
          loading: false,
          error: (e as Error).message,
          diff: null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.fetchPack, props.kind, props.packId, props.fromVersion, props.toVersion]);

  async function handleAccept(): Promise<void> {
    setAccepting(true);
    try {
      await props.onAccept();
    } finally {
      setAccepting(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: native <dialog> requires imperative showModal(); ARIA dialog pattern is intentional
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-diff-title"
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <h2 id="update-diff-title" className="text-lg font-bold">
          {t("update.viewer.title", {
            packId: props.packId,
            from: props.fromVersion,
            to: props.toVersion,
          })}
        </h2>

        {state.loading && <p className="mt-4">{t("update.viewer.loading")}</p>}
        {state.error && (
          <p className="mt-4 text-red-700">{t("update.viewer.error", { reason: state.error })}</p>
        )}
        {state.diff && diffIsEmpty(state.diff) && (
          <p className="mt-4 italic text-muted-foreground">{t("update.viewer.empty")}</p>
        )}
        {state.diff && !diffIsEmpty(state.diff) && (
          <div className="mt-4 space-y-4 text-sm">
            {state.diff.added.length > 0 && (
              <section>
                <h3 className="font-semibold text-emerald-700">
                  {t("update.viewer.added", { count: state.diff.added.length })}
                </h3>
                <ul className="ml-4 list-disc font-mono text-xs">
                  {state.diff.added.map((k) => (
                    <li key={k}>{k}</li>
                  ))}
                </ul>
              </section>
            )}
            {state.diff.removed.length > 0 && (
              <section>
                <h3 className="font-semibold text-rose-700">
                  {t("update.viewer.removed", { count: state.diff.removed.length })}
                </h3>
                <ul className="ml-4 list-disc font-mono text-xs">
                  {state.diff.removed.map((k) => (
                    <li key={k}>{k}</li>
                  ))}
                </ul>
              </section>
            )}
            {state.diff.changed.length > 0 && (
              <section>
                <h3 className="font-semibold text-amber-700">
                  {t("update.viewer.changed", { count: state.diff.changed.length })}
                </h3>
                <ul className="ml-4 list-disc space-y-1 font-mono text-xs">
                  {state.diff.changed.map((c) => (
                    <li key={c.key}>
                      <div>{c.key}</div>
                      <div className="text-rose-700">- {c.prev}</div>
                      <div className="text-emerald-700">+ {c.next}</div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border px-3 py-1 text-sm"
            onClick={props.onCancel}
            disabled={accepting}
          >
            {t("update.viewer.cancel")}
          </button>
          <button
            type="button"
            className="rounded bg-emerald-600 px-3 py-1 text-sm text-white disabled:opacity-50"
            onClick={handleAccept}
            disabled={accepting || state.loading || !!state.error}
          >
            {accepting ? t("update.viewer.accepting") : t("update.viewer.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
